import { useEffect, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PLATFORMS, Platform, CreateVersionMappingInput, UpdateVersionMappingInput, VersionMapping } from './version-mappings-types';
import { Game } from '@/features/games/games-types';
import { useGameBaseUrls } from '@/features/game-base-urls/game-base-urls-queries';

const SEMVER_REGEX = /^[0-9]+\.[0-9]+\.[0-9]+(-[A-Za-z0-9.-]+)?(\+[A-Za-z0-9.-]+)?$/;

// Red Team F#5: z.enum is a NEW zod pattern in the dashboard (zero existing
// uses before Phase 4). The PLATFORMS array must equal the API's PLATFORMS
// set-wise and length-wise — verified by a Phase 5 grep.
const schema = z.object({
  game_id: z.string().uuid('Game is required'),
  platform: z.enum(['ios', 'android', 'web', 'harmonyos']),
  min_version: z.string().regex(SEMVER_REGEX, 'Must be valid semver (e.g. 2.0.0)'),
  max_version: z
    .string()
    .regex(SEMVER_REGEX, 'Must be valid semver or empty')
    .optional()
    .or(z.literal('')),
  base_url: z
    .string()
    .url('Must be a valid URL')
    .startsWith('https://', 'Must be HTTPS'),
  deprecation_date: z.string().optional().or(z.literal('')),
  is_active: z.boolean().default(true),
});
type FormData = z.infer<typeof schema>;

interface VersionMappingFormProps {
  initial?: VersionMapping;
  games: Game[];
  onSubmit: (data: CreateVersionMappingInput | UpdateVersionMappingInput) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function VersionMappingForm({
  initial,
  games,
  onSubmit,
  onCancel,
  isSubmitting,
}: VersionMappingFormProps): JSX.Element {
  const isEdit = !!initial;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    // Cast: zod default() output type widens form type; runtime is correct.
    resolver: zodResolver(schema) as unknown as Resolver<FormData>,
    defaultValues: initial
      ? {
          game_id: initial.game_id,
          platform: initial.platform,
          min_version: initial.min_version,
          max_version: initial.max_version ?? '',
          base_url: initial.base_url,
          deprecation_date: initial.deprecation_date?.slice(0, 16) ?? '',
          is_active: initial.is_active,
        }
      : { game_id: '', platform: 'android', is_active: true },
  });

  useEffect(() => {
    if (initial) {
      reset({
        game_id: initial.game_id,
        platform: initial.platform,
        min_version: initial.min_version,
        max_version: initial.max_version ?? '',
        base_url: initial.base_url,
        deprecation_date: initial.deprecation_date?.slice(0, 16) ?? '',
        is_active: initial.is_active,
      });
    }
  }, [initial, reset]);

  const isActive = watch('is_active');
  const selectedGameId = watch('game_id');
  const selectedPlatform = watch('platform');
  const baseUrl = watch('base_url');

  // Admin-only catalog of URLs for the selected game. Used to populate the
  // base_url dropdown instead of free-text input.
  const { data: gameBaseUrls } = useGameBaseUrls(selectedGameId ?? '');

  // Determine render mode for the base_url field:
  //  - Edit: use Select ONLY if the current base_url is already in the catalog
  //    (don't lose the operator's saved value by forcing a re-pick).
  //  - Create: use Select as soon as the catalog has any entries.
  // Otherwise, fall back to the free-text Input (legacy / no catalog yet).
  const activeCatalog = useMemo(
    () => (gameBaseUrls ?? []).filter((u) => u.is_active),
    [gameBaseUrls],
  );
  const useCatalogPicker =
    activeCatalog.length > 0 && (!isEdit || activeCatalog.some((u) => u.base_url === baseUrl));

  // Group active catalog rows by environment (sorted by environment code).
  // Order is stable so the dropdown reads consistently across renders.
  const catalogByEnv = useMemo(() => {
    const groups = new Map<string, { code: string; name: string; urls: typeof activeCatalog }>();
    for (const u of activeCatalog) {
      const envCode = u.environment?.code ?? 'unknown';
      const envName = u.environment?.name ?? 'Unknown';
      let bucket = groups.get(envCode);
      if (!bucket) {
        bucket = { code: envCode, name: envName, urls: [] };
        groups.set(envCode, bucket);
      }
      bucket.urls.push(u);
    }
    return [...groups.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [activeCatalog]);

  function handleFormSubmit(data: FormData): void {
    if (isEdit) {
      // Red Team F#1: PATCH payload must NOT include `platform`. The global
      // API ValidationPipe has forbidNonWhitelisted=true, so including it
      // returns 400. We omit the key entirely (not just undefined).
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { platform: _stripped, game_id: _gid, ...rest } = data;
      // Empty strings would fail BE @IsOptional + @Matches / @IsDateString
      // (IsOptional only skips when value is null/undefined). Convert '' → null.
      void onSubmit({
        ...rest,
        max_version: rest.max_version || null,
        deprecation_date: rest.deprecation_date || null,
      } as UpdateVersionMappingInput);
    } else {
      void onSubmit({
        game_id: data.game_id ?? '',
        platform: data.platform,
        min_version: data.min_version,
        max_version: data.max_version || null,
        base_url: data.base_url,
        deprecation_date: data.deprecation_date || null,
        is_active: data.is_active,
      } as CreateVersionMappingInput);
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="game_id">Game</Label>
          <Select
            value={selectedGameId ?? ''}
            onValueChange={(v) => setValue('game_id', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a game" />
            </SelectTrigger>
            <SelectContent>
              {games.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.package_name} ({g.name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.game_id && (
            <p className="text-sm text-destructive">Game is required</p>
          )}
        </div>
      )}
      <div className="space-y-2">
        {/* Red Team F#1: platform is immutable on PATCH. The select is
            disabled on edit; helper text explains the rationale. */}
        <Label htmlFor="platform">Platform</Label>
        <Select
          value={selectedPlatform ?? 'android'}
          onValueChange={(v) => setValue('platform', v as Platform)}
          disabled={isEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a platform" />
          </SelectTrigger>
          <SelectContent>
            {PLATFORMS.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.platform && (
          <p className="text-sm text-destructive">Platform is required</p>
        )}
        {isEdit && (
          <p className="text-xs text-muted-foreground">
            Platform is locked after creation. To change, delete and recreate.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="min_version">Min version</Label>
          <Input id="min_version" placeholder="1.0.0" {...register('min_version')} />
          {errors.min_version && (
            <p className="text-sm text-destructive">{errors.min_version.message as string}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="max_version">Max version (optional)</Label>
          <Input id="max_version" placeholder="2.0.0" {...register('max_version')} />
          {errors.max_version && (
            <p className="text-sm text-destructive">{errors.max_version.message as string}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="base_url">Base URL (HTTPS only)</Label>
        {useCatalogPicker ? (
          <>
            <Select
              value={baseUrl ?? ''}
              onValueChange={(v) => setValue('base_url', v, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a base URL from the catalog" />
              </SelectTrigger>
              <SelectContent>
                {catalogByEnv.map((group) => (
                  <SelectGroup key={group.code}>
                    <SelectLabel>{group.name}</SelectLabel>
                    {group.urls.map((u) => (
                      <SelectItem key={u.id} value={u.base_url}>
                        {u.base_url}
                        {u.is_deprecated ? ' ⚠ Deprecated' : ''}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Picked from the catalog at{' '}
              <code>/games/{selectedGameId}/base-urls</code>. {useCatalogPicker && (
                <>
                  Inactive entries are hidden. Deprecated entries are shown with a{' '}
                  <span className="rounded bg-yellow-100 px-1 text-yellow-900">⚠ Deprecated</span>{' '}
                  badge but still selectable.
                </>
              )}
            </p>
          </>
        ) : (
          <>
            <Input
              id="base_url"
              type="url"
              placeholder="https://api.example.com"
              {...register('base_url')}
            />
            <p className="text-xs text-muted-foreground">
              No catalog entries yet for this game. Type a URL or add entries at{' '}
              <code>/games/{selectedGameId}/base-urls</code> to enable the dropdown.
            </p>
          </>
        )}
        {errors.base_url && (
          <p className="text-sm text-destructive">{errors.base_url.message as string}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="deprecation_date">Deprecation date (optional)</Label>
        <Input id="deprecation_date" type="datetime-local" {...register('deprecation_date')} />
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="is_active"
          checked={isActive}
          onCheckedChange={(c) => setValue('is_active', c)}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
